<template>
  <div class="scanner-delivery-in-store">
    <div class="scanner-delivery-in-store__scan-zone">
      <div v-if="loader" class="scanner-delivery-in-store__loader">
        <b-spinner variant="light" />
      </div>
      <qr-scanner
        v-if="camerasReady"
        ref="qrScanner"
        :camera-id="selectedCameraId"
        @scan="onDetect"
      />
    </div>

    <div class="scanner-delivery-in-store__bottom">
      <div class="scanner-delivery-in-store__bottom-actions">
        <b-button
          size="sm"
          class="scanner-delivery-in-store__scan-count-btn"
          :disabled="!scannedItems.length"
          @click="showScannedModal = true"
        >
          <span class="material-icons">check_circle</span>
          <span>{{ successfulScansCount }}</span>
          <span class="scanner-delivery-in-store__scan-count-label">
            escane{{ successfulScansCount === 1 ? 'o' : 'os' }} exitoso{{ successfulScansCount === 1 ? '' : 's' }}
          </span>
        </b-button>
      </div>

      <b-modal
        v-model="showScannedModal"
        title="Envíos recepcionados"
        ok-only
        ok-title="Cerrar"
        size="lg"
        modal-class="scanned-items-modal"
      >
        <scanned-items-list
          :items="scannedItems"
          :fields="fields"
          @clear="clearScanned"
        >
          <template #cell(success)="slotProps">
            <b-badge :variant="slotProps.item.success ? 'success' : 'danger'" style="font-size: 0.8rem;">
              {{ slotProps.item.success ? 'OK' : 'Error' }}
            </b-badge>
          </template>
          <template #cell(message)="slotProps">
            <span class="scanner-delivery-in-store__msg">
              {{ slotProps.item.message || '—' }}
            </span>
          </template>
          <template #cell(scannedAt)="slotProps">
            <span class="scanner-delivery-in-store__time">
              {{ formatDateTimeOrDash(slotProps.item.scannedAt) }}
            </span>
          </template>
        </scanned-items-list>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { Html5Qrcode } from 'html5-qrcode'
import { mapActions } from 'vuex'
import QrScanner from '../common/QrScanner.vue'
import ScannedItemsList from '../common/ScannedItemsList.vue'
import { formatDateTimeOrDash } from '../../utils/dateUtils'

const CAMERA_STORAGE_KEY = 'scanner_selected_camera_id'

/**
 * Componente reusable para flujos de escaneo de deliveries.
 *
 * Diseñado para soportar múltiples tipos de eventos (recepción en tienda,
 * entrega a cliente, etc.) vía la prop `scanType`. Hoy sólo se usa para
 * `pickedUpAtStore` (recepción); mañana se pueden agregar otros tipos
 * extendiendo la lógica del scanCode() según el tipo.
 *
 * Props:
 *   - scanType: identificador del flujo (e.g. 'pickedUpAtStore').
 *               Se usa para la key de localStorage y para branching futuro.
 *   - initialCameraId: opcional, cámara a usar al montar.
 *
 * Persistencia:
 *   Los items escaneados viven en localStorage bajo la key
 *   `scan-{scanType}` con auto-clear diario (key `scan-{scanType}-date`).
 *   El componente carga/guarda la lista automáticamente.
 */
export default {
  name: 'ScannerDeliveryInStore',
  components: { QrScanner, ScannedItemsList },
  props: {
    scanType: {
      type: String,
      required: true
    },
    initialCameraId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loader: false,
      cameras: [],
      camerasReady: false,
      selectedCameraId: this.initialCameraId || localStorage.getItem(CAMERA_STORAGE_KEY) || null,
      scannedItems: [],
      showScannedModal: false,
      fields: [
        { key: 'id', label: 'Id' },
        { key: 'success', label: 'Estado' },
        { key: 'message', label: 'Detalle' },
        { key: 'scannedAt', label: 'Hora' }
      ]
    }
  },
  computed: {
    successfulScansCount() {
      return this.scannedItems.filter(item => item.success === true).length
    }
  },
  watch: {
    selectedCameraId(newId) {
      if (newId && typeof newId === 'string') {
        localStorage.setItem(CAMERA_STORAGE_KEY, newId)
      }
    }
  },
  async mounted() {
    await this.loadCameras()
    this.camerasReady = true
  },
  methods: {
    ...mapActions(['scanDelivery']),
    formatDateTimeOrDash,
    async loadCameras() {
      try {
        this.cameras = await Html5Qrcode.getCameras()
        if (this.cameras.length && !this.selectedCameraId) {
          const backCamera = this.cameras.find(c =>
            /back|rear|environment/i.test(c.label || '')
          )
          this.selectedCameraId = backCamera ? backCamera.id : this.cameras[0].id
        }
      } catch (error) {
        // El componente QrScanner maneja el error internamente y muestra CameraPermission
        this.cameras = []
      }
    },

    /**
     * Hook público: cambiar de cámara desde el padre.
     * El padre pasa el id de cámara seleccionado y este componente lo
     * propaga al QrScanner vía watch.
     */
    setCamera(cameraId) {
      this.selectedCameraId = cameraId
    },

    clearScanned({ silent } = {}) {
      this.scannedItems = []
      if (!silent) {
        this.$bvToast.toast('Lista de escaneos limpiada', {
          title: 'Hecho',
          variant: 'success'
        })
      }
    },

    /**
     * Hoy sólo soporta `scanType === 'pickedUpAtStore'`. Para agregar
     * otros eventos de escaneo, extender este método con un switch
     * sobre scanType y disparar la acción correspondiente.
     */
    async scanCode(qrContent) {
      try {
        const response = await this.scanDelivery({ qrContent, mode: 'qr' })
        const data = (response && response.data) || {}
        const delivery = data.delivery || {}
        return {
          ok: response && response.status === 200,
          alreadyScanned: data.alreadyScanned === true,
          deliveryId: delivery.id,
          message: data.alreadyScanned
            ? 'Ya estaba marcado'
            : `Marcado: #${delivery.id || ''}`
        }
      } catch (err) {
        const message = err && err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'Error de red'
        return { ok: false, message }
      }
    },

    async onDetect(qrContent) {
      if (this.loader) return

      // Deduplicación por contenido exacto del QR en la sesión.
      if (this.scannedItems.some(item => item.qrContent === qrContent)) {
        return this.$bvToast.toast('Este QR ya lo escaneaste en esta sesión', {
          title: 'Duplicado',
          variant: 'warning'
        })
      }

      this.loader = true
      try {
        const result = await this.scanCode(qrContent)
        const entry = {
          qrContent,
          id: result.deliveryId || null,
          success: result.ok,
          message: result.message,
          scannedAt: new Date().toISOString()
        }
        this.scannedItems = [entry, ...this.scannedItems]

        if (result.ok) {
          this.$bvToast.toast(result.message || 'Marcado correctamente', {
            title: 'Éxito',
            variant: result.alreadyScanned ? 'warning' : 'success'
          })
        } else {
          this.$bvToast.toast(result.message || 'No se pudo procesar el QR', {
            title: 'Error',
            variant: 'danger'
          })
        }
      } finally {
        this.loader = false
      }
    },

    _today() {
      const d = new Date()
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    }
  }
}
</script>

<style scoped>
.scanner-delivery-in-store {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100% !important;
}

.scanner-delivery-in-store__scan-zone {
  flex: 1;
  position: relative;
  min-height: 0;
  display: flex;
}

.scanner-delivery-in-store__loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 5;
}

.scanner-delivery-in-store__bottom {
  padding: 0.75rem;
  flex-shrink: 0;
}

.scanner-delivery-in-store__bottom-actions {
  display: flex;
  justify-content: center;
}

.scanner-delivery-in-store__scan-count-btn {
  display: inline-flex !important;
  align-items: center !important;
  gap: 0.4rem;
  font-weight: 600;
  border-radius: 20px !important;
  padding: 0.45rem 0.9rem !important;

  .material-icons {
    font-size: 18px;
  }
}

.scanner-delivery-in-store__scan-count-label {
  font-weight: 400;
  opacity: 0.85;
  margin-left: 0.2rem;
  font-size: 0.85rem;
}

/* El modal es claro pero la tabla interna sigue con tema oscuro para
   mantener consistencia con la UI del scanner. */
.scanned-items-modal .modal-body {
}

.scanner-delivery-in-store__msg {
  font-size: 0.8rem;
}
</style>